import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BaseComponent} from "../../shared/base.component";
import {catchError, concatMap, first, takeUntil, map, tap, startWith, filter} from "rxjs/operators";
import {ApiService} from "../../services/api/api.service";
import {Observable, of} from "rxjs";
import {Job} from "../../_models/jobs/job";
import {HeaderDataService} from "../../services/header_data.service";
import {BackgroundJobService, JobPollResponse} from "../../services/background-job.service";
import {KeyMap, ModelID} from "../../_typing/generic-types";
import {NotificationService} from "../../services/notification.service";
import {JobTypePipe} from "../job-type.pipe";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {CommonModule} from "@angular/common";
import {PrettyDateComponent} from "../pretty-date/pretty-date.component";
import {JobStatusComponent} from "../job-status/job-status.component";
import {MatTooltip} from "@angular/material/tooltip";
import {JobErrorOutputComponent} from "./job-error-output/job-error-output.component";
import {
    SeriesCorrectionJobInputDataComponent
} from "../series-correction/series-correction-job-input-data/series-correction-job-input-data.component";
import {
    SeriesCorrectionJobOutputDataComponent
} from "../series-correction/series-correction-job-output-data/series-correction-job-output-data.component";


export interface BatchSeriesAdjustmentData {
    //TODO what does this look like?
}

export interface BatchSeriesAdjustmentResponse extends JobPollResponse {
    data?: BatchSeriesAdjustmentOutput;
}

export interface BatchSeriesAdjustmentOutput {
    data: KeyMap<BatchSeriesAdjustmentData>;
    result: { series_id: ModelID, value: number }[];
}

@Component({
    selector: 'src-job-detail',
    templateUrl: './job-detail.component.html',
    styleUrls: ['./job-detail.component.scss', '../polling-indicator.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        JobTypePipe,
        MatCard,
        MatCardHeader,
        MatCardContent,
        MatCardTitle,
        MatSlideToggle,
        PrettyDateComponent,
        JobStatusComponent,
        JobErrorOutputComponent,
        SeriesCorrectionJobInputDataComponent,
        SeriesCorrectionJobOutputDataComponent,
        MatTooltip
    ],
})
export class JobDetailComponent extends BaseComponent {

    $job: Observable<Job<any, any, any>>;
    was_missing: boolean = false;
    job_id: string;
    reportMessage: string = ""
    // Whether view for input data is set to JSON or pretty render.
    inputViewJson: boolean = false;
    // Whether view for output data is set to JSON or pretty render.
    outputViewJson: boolean = true;
    // If missing JSON on the input field, disable JSON toggle and show missing data.
    outputMissing: boolean = true;

    polling = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private api: ApiService,
        private headerData: HeaderDataService,
        private jobService: BackgroundJobService,
        private notification: NotificationService,
        private changeDetectorRef: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.headerData.title = "Detail for Job";
        this.$job = this.activatedRoute.params.pipe(
            first(),
            concatMap(params => {
                const id: string = params['jobId'];
                // TODO add error handling
                this.job_id = id;
                return this.api.job.getById(id);
            }),
            catchError((error, caught) => {
                return of(null);
            }),
            concatMap(response => {
                if (response == null) {
                    this.was_missing = true;
                    this.reportMessage = "Job not found";
                    return of(null);
                }
                this.headerData.title = "Detail for Job " + this.job_id;
                let job = response.data;
                if (job.attributes.output_data) {
                    this.outputViewJson = false;
                    this.outputMissing = false;
                    this.reportMessage = job.attributes.output_data.message;
                    return of(job);
                }
                return this.pollBatchCorrectionRun().pipe(
                    startWith(job), // Emit the initial job first
                    map((result: BatchSeriesAdjustmentResponse) => {
                        // Only update status if result is from polling (has processing property)
                        if (result && 'processing' in result) {
                            //polling error is different to job.status=error. Must job be refetched?
                            const status = result.status ? 'completed' : result.processing ? 'processing' : 'error';
                            job.attributes.status = status;
                            //job.attributes.output_data = result.data;
                        }
                        this.changeDetectorRef.markForCheck();
                        return job;
                    })
                );
            })
        );
    }

    pollBatchCorrectionRun(): Observable<BatchSeriesAdjustmentResponse> {
        this.polling = true;
        this.reportMessage = "The job is still in progress."
        return this.jobService.createJobPoll(this.job_id).pipe(
            takeUntil(this.onDestroy),
            tap((jobResponse: JobPollResponse) => {
                if (jobResponse.processing) {
                    this.reportMessage = "The job is still in progress.";
                    console.log('Job ' + this.job_id + ': ' + jobResponse.message);
                }
            }),
            // Only emit the final result when job is complete
            filter((jobResponse: JobPollResponse) => jobResponse.status || !jobResponse.processing),
            tap((completedJob: JobPollResponse) => {
                this.polling = false;
                this.reportMessage = completedJob.data?.message ?? completedJob.message;
                if (completedJob.status) {
                    console.log('Job ' + this.job_id + ': ' + completedJob.message);
                } else {
                    this.notification.openError('Error running job: ' + completedJob.message);
                }
            }),
            map((jobResponse: JobPollResponse) => jobResponse as BatchSeriesAdjustmentResponse),
            catchError((error) => {
                this.reportMessage = "Error running job";
                this.polling = false;
                this.notification.openError('Error running job: ' + (error.message || error));
                return of(null);
            })
        );
    }
}
